import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Brain, Logo, Check, X, Sparkles } from '../components/Icons.jsx';
import DrillResultBar from '../components/DrillResultBar.jsx';
import StuckPopIn from '../components/StuckPopIn.jsx';

const easyScenarios = [
  {
    setup: 'A clinic surveys 120 patients. 70 take medication A. 55 take medication B. 30 take both.',
    regions: [
      { label: 'A only', answer: 40, working: '70 − 30' },
      { label: 'B only', answer: 25, working: '55 − 30' },
      { label: 'Both', answer: 30, working: 'Given' },
      { label: 'Neither', answer: 25, working: '120 − 40 − 25 − 30' }
    ]
  },
  {
    setup: 'A school has 200 sixth-formers. 140 do Chemistry. 110 do Biology. 85 do both.',
    regions: [
      { label: 'Chem only', answer: 55, working: '140 − 85' },
      { label: 'Bio only', answer: 25, working: '110 − 85' },
      { label: 'Both', answer: 85, working: 'Given' },
      { label: 'Neither', answer: 35, working: '200 − 55 − 25 − 85' }
    ]
  },
  {
    setup: '80 medics in a survey. 50 reported high caffeine intake. 35 reported poor sleep. 25 reported both.',
    regions: [
      { label: 'Caffeine only', answer: 25, working: '50 − 25' },
      { label: 'Poor sleep only', answer: 10, working: '35 − 25' },
      { label: 'Both', answer: 25, working: 'Given' },
      { label: 'Neither', answer: 20, working: '80 − 25 − 10 − 25' }
    ]
  },
  {
    setup: 'Of 90 trainees: 64 passed Station 1. 48 passed Station 2. 30 passed both.',
    regions: [
      { label: 'S1 only', answer: 34, working: '64 − 30' },
      { label: 'S2 only', answer: 18, working: '48 − 30' },
      { label: 'Both', answer: 30, working: 'Given' },
      { label: 'Neither', answer: 8, working: '90 − 34 − 18 − 30' }
    ]
  },
  {
    setup: '150 students: 95 own a laptop, 70 own a tablet, 45 own both.',
    regions: [
      { label: 'Laptop only', answer: 50, working: '95 − 45' },
      { label: 'Tablet only', answer: 25, working: '70 − 45' },
      { label: 'Both', answer: 45, working: 'Given' },
      { label: 'Neither', answer: 30, working: '150 − 50 − 25 − 45' }
    ]
  }
];

const hardScenarios = [
  {
    setup: 'A revision app tracks 300 candidates. 180 use UCAT, 150 use BMAT, 90 use PS. 70 use UCAT+BMAT, 50 use UCAT+PS, 40 use BMAT+PS. 30 use all three.',
    regions: [
      { label: 'UCAT only', answer: 90, working: '180 − 70 − 50 + 30' },
      { label: 'BMAT only', answer: 70, working: '150 − 70 − 40 + 30' },
      { label: 'PS only', answer: 30, working: '90 − 50 − 40 + 30' },
      { label: 'UCAT + BMAT only', answer: 40, working: '70 − 30' },
      { label: 'UCAT + PS only', answer: 20, working: '50 − 30' },
      { label: 'BMAT + PS only', answer: 10, working: '40 − 30' },
      { label: 'All three', answer: 30, working: 'Given' },
      { label: 'None', answer: 10, working: '300 − sum of all regions' }
    ]
  },
  {
    setup: '500 admissions interviews scored on Communication (C), Empathy (E), Knowledge (K). 320 passed C, 280 passed E, 240 passed K. 200 passed C+E, 160 passed C+K, 150 passed E+K. 120 passed all three.',
    regions: [
      { label: 'C only', answer: 80, working: '320 − 200 − 160 + 120' },
      { label: 'E only', answer: 50, working: '280 − 200 − 150 + 120' },
      { label: 'K only', answer: 50, working: '240 − 160 − 150 + 120' },
      { label: 'C + E only', answer: 80, working: '200 − 120' },
      { label: 'C + K only', answer: 40, working: '160 − 120' },
      { label: 'E + K only', answer: 30, working: '150 − 120' },
      { label: 'All three', answer: 120, working: 'Given' },
      { label: 'None', answer: 50, working: '500 − sum of regions' }
    ]
  },
  {
    setup: '240 medical students. 140 attend GP placements, 120 attend hospice placements, 90 attend surgery placements. 70 attend GP+hospice, 50 attend GP+surgery, 40 attend hospice+surgery. 30 attend all three.',
    regions: [
      { label: 'GP only', answer: 50, working: '140 − 70 − 50 + 30' },
      { label: 'Hospice only', answer: 40, working: '120 − 70 − 40 + 30' },
      { label: 'Surgery only', answer: 30, working: '90 − 50 − 40 + 30' },
      { label: 'GP + Hospice only', answer: 40, working: '70 − 30' },
      { label: 'GP + Surgery only', answer: 20, working: '50 − 30' },
      { label: 'Hospice + Surgery only', answer: 10, working: '40 − 30' },
      { label: 'All three', answer: 30, working: 'Given' },
      { label: 'None', answer: 20, working: '240 − sum of regions' }
    ]
  }
];

export default function VennArchitect() {
  const [stage, setStage] = useState('intro');
  const [difficulty, setDifficulty] = useState('easy');
  const [ucatsen, setUcatsen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [startMs, setStartMs] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [wrongStreak, setWrongStreak] = useState(0);

  const scenarios = difficulty === 'easy' ? easyScenarios : hardScenarios;
  const s = scenarios[idx];
  const targetSecPerQ = difficulty === 'easy' ? 60 : 90;
  const allowed = ucatsen ? Math.round(targetSecPerQ * 1.25) : targetSecPerQ;

  useEffect(() => {
    if (stage !== 'drill') return;
    const t = setInterval(() => setElapsed(Date.now() - startMs), 250);
    return () => clearInterval(t);
  }, [stage, startMs]);

  const start = () => {
    setIdx(0); setAnswers({}); setRevealed(false);
    setStartMs(Date.now()); setElapsed(0); setWrongStreak(0); setStage('drill');
  };
  const submit = () => {
    let c = 0;
    s.regions.forEach((r, i) => { if (Number(answers[i]) === r.answer) c++; });
    const perfect = c === s.regions.length;
    setWrongStreak((w) => perfect ? 0 : w + 1);
    setRevealed(true);
  };
  const next = () => {
    if (idx < scenarios.length - 1) { setIdx(idx + 1); setAnswers({}); setRevealed(false); }
    else { setElapsed(Date.now() - startMs); setStage('done'); }
  };
  const updateAnswer = (i, v) => setAnswers({ ...answers, [i]: v });

  const correctCount = useMemo(() => {
    if (!revealed) return null;
    let c = 0;
    s.regions.forEach((r, i) => { if (Number(answers[i]) === r.answer) c++; });
    return c;
  }, [answers, revealed, s]);

  return (
    <DrillShell title="Venn architect">
      {stage === 'intro' && (
        <Intro
          subtest="DM"
          title="Venn architect"
          body="Translate prose into Venn region counts. The single biggest source of marks (and lost marks) in Decision Making."
          how={['Read each scenario carefully', 'Type the count for each region', 'Submit to see the correct counts + working']}
          difficulty={difficulty} setDifficulty={setDifficulty}
          difficultyOptions={[
            { v: 'easy', label: 'Two circles', sub: '5 scenarios · 4 regions each' },
            { v: 'hard', label: 'Three circles', sub: '3 scenarios · 8 regions each' }
          ]}
          ucatsen={ucatsen} setUcatsen={setUcatsen}
          onStart={start}
        />
      )}

      {stage === 'drill' && <StuckPopIn wrongStreak={wrongStreak} slug="venn-architect" sectionLabel="venn architect" />}
      {stage === 'drill' && s && (
        <main style={mainStyle}>
          <ProgressBar idx={idx} total={scenarios.length} elapsed={elapsed} />

          <div style={{ fontSize: 'clamp(16px, 2.5vw, 18px)', lineHeight: 1.55, marginBottom: 22, color: 'var(--text)' }}>{s.setup}</div>

          <div className="r-grid-2">
            {s.regions.map((r, i) => {
              const v = answers[i];
              const numVal = Number(v);
              const correct = revealed && numVal === r.answer;
              const wrong = revealed && numVal !== r.answer;
              return (
                <div key={i} style={{
                  padding: '14px 16px', borderRadius: 12,
                  border: `1px solid ${correct ? 'var(--live-green)' : wrong ? '#fecaca' : 'var(--border)'}`,
                  background: correct ? '#e7f7ef' : wrong ? '#fee2e2' : '#fff'
                }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{r.label}</div>
                  <input
                    type="number"
                    value={v ?? ''} disabled={revealed}
                    onChange={(e) => updateAnswer(i, e.target.value)}
                    placeholder="—"
                    style={{
                      marginTop: 6, width: '100%', padding: '10px 12px',
                      fontSize: 20, fontWeight: 700,
                      border: '1px solid var(--border-strong)', borderRadius: 8,
                      background: revealed ? 'transparent' : '#fff'
                    }}
                  />
                  {revealed && (
                    <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-2)' }}>
                      Correct: <strong>{r.answer}</strong> <span style={{ color: 'var(--text-muted)' }}>· {r.working}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 22, display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Target {allowed}s {ucatsen && '(UCATSEN)'} · elapsed {Math.round(elapsed / 1000)}s
            </div>
            {!revealed
              ? <button onClick={submit} className="btn btn-primary">Check answers <Check width="16" height="16" /></button>
              : <button onClick={next} className="btn btn-primary">{idx === scenarios.length - 1 ? 'Finish' : 'Next scenario'} <ArrowRight width="16" height="16" /></button>}
          </div>
        </main>
      )}

      {stage === 'done' && (
        <Done subtest="DM" title="Venn architect" stats={[
          { label: 'Scenarios done', value: scenarios.length },
          { label: 'Time', value: formatTime(elapsed) },
          { label: 'Mode', value: difficulty === 'easy' ? 'Two-circle' : 'Three-circle' }
        ]} onRestart={start}
          extra={<DrillResultBar slug="venn-architect" score={scenarios.length} accuracyPct={null} metadata={{ difficulty, ucatsen, elapsedMs: elapsed }} />}
        />
      )}
    </DrillShell>
  );
}

// ===== shared shells (reused by other drills) =====

function DrillShell({ title, children }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={drillHeader}>
        <Link to="/practice" style={drillExit}>
          <ArrowLeft width="16" height="16" />Exit
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="26" height="26" onLight />
          <strong style={{ fontSize: 14, color: 'var(--brand-deep)', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' }}>{title}</strong>
        </div>
        <div style={{ width: 60 }} />
      </header>
      {children}
    </div>
  );
}

export function Intro({ subtest, title, body, how, difficulty, setDifficulty, difficultyOptions, ucatsen, setUcatsen, onStart, extra }) {
  return (
    <main style={mainStyle}>
      <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{subtest} drill</div>
      <h1 style={{ margin: '6px 0 8px', fontSize: 'clamp(28px, 5vw, 36px)', letterSpacing: '-0.01em' }}>{title}</h1>
      <p style={{ color: 'var(--text-2)', fontSize: 16, lineHeight: 1.6, maxWidth: 680 }}>{body}</p>

      {how && (
        <ul style={{ margin: '14px 0 22px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--text-2)', fontSize: 14.5 }}>
          {how.map((h, i) => (
            <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--brand-purple)', flexShrink: 0, marginTop: 4 }}>▸</span>{h}
            </li>
          ))}
        </ul>
      )}

      {difficultyOptions && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Difficulty</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {difficultyOptions.map((opt) => {
              const sel = difficulty === opt.v;
              return (
                <button key={opt.v} onClick={() => setDifficulty(opt.v)} style={difficultyPill(sel)}>
                  <strong>{opt.label}</strong>
                  {opt.sub && <span style={{ fontWeight: 500, opacity: 0.7, fontSize: 12, marginTop: 2 }}>{opt.sub}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {extra}

      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '12px 14px', background: ucatsen ? 'var(--accent-soft)' : 'var(--card-soft)', border: `1px solid ${ucatsen ? 'var(--brand-purple)' : 'var(--border)'}`, borderRadius: 10, maxWidth: 460 }}>
          <input type="checkbox" checked={ucatsen} onChange={(e) => setUcatsen(e.target.checked)} style={{ width: 18, height: 18, accentColor: 'var(--brand-purple)' }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>UCATSEN mode</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>+25% time per question. Mirrors the real UCATSEN accommodation.</div>
          </div>
        </label>
      </div>

      <button onClick={onStart} className="btn btn-primary" style={{ fontSize: 16, padding: '12px 22px' }}>
        Start drill <ArrowRight width="16" height="16" />
      </button>
    </main>
  );
}

export function Done({ subtest, title, stats, onRestart, extra }) {
  return (
    <main style={mainStyle}>
      <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{subtest} drill · complete</div>
      <h1 style={{ margin: '6px 0 18px', fontSize: 'clamp(28px, 5vw, 36px)' }}>{title}</h1>

      <div className="r-grid-3" style={{ marginBottom: 22 }}>
        {stats.map((s) => (
          <div key={s.label} className="card">
            <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>{s.label}</div>
            <div style={{ fontSize: 'clamp(24px, 4vw, 32px)', fontWeight: 700, color: 'var(--brand-deep)', marginTop: 4 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {extra}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={onRestart} className="btn btn-primary">Run again <Sparkles width="14" height="14" /></button>
        <Link to="/practice" className="btn btn-secondary">Back to Practice</Link>
      </div>
    </main>
  );
}

function ProgressBar({ idx, total, elapsed }) {
  const pct = (idx / total) * 100;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
        <span style={{ fontWeight: 600 }}>Question {idx + 1} of {total}</span>
        <span>{formatTime(elapsed)}</span>
      </div>
      <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', transition: 'width 250ms ease' }} />
      </div>
    </div>
  );
}

export function formatTime(ms) {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const mainStyle = { maxWidth: 880, margin: '0 auto', padding: 'clamp(20px, 4vw, 32px)' };
const drillHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#fff', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 10 };
const drillExit = { display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', textDecoration: 'none', fontSize: 14, fontWeight: 600, minWidth: 60 };
export function difficultyPill(active) {
  return {
    padding: '12px 16px', borderRadius: 12, fontSize: 14, cursor: 'pointer',
    border: `2px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`,
    background: active ? 'var(--accent-soft)' : '#fff',
    color: 'var(--text)', display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
    transition: 'all 150ms ease', minWidth: 140
  };
}
